import React, { useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarQuickFilter,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTheme } from "@mui/material";

// Sample Data for Shift Requests
const requestRows = Array.from({ length: 50 }, (_, index) => ({
  id: index + 1,
  employee: `Employee ${index + 1}`,
  avatar: `https://i.pravatar.cc/150?img=${index + 1}`,
  requestDate: new Date(
    2024,
    Math.floor(Math.random() * 12),
    Math.floor(Math.random() * 28)
  ).toLocaleDateString(),
  status: ["Pending", "Completed", "Rejected"][index % 3],
}));

const RequestShifts: React.FC = () => {
  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const handleOpenModal = (row: any) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleConfirm = () => {
    console.log("Confirmed request for:", selectedRow);
    handleCloseModal();
  };

  // Media Query for Responsive Font Scaling
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // DataGrid Columns
  const requestColumns: GridColDef[] = [
    {
      field: "avatar",
      headerName: "",
      minWidth: 70,
      flex: 0.5,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          alt="Avatar"
          sx={{ width: 30, height: 30 }}
        />
      ),
      sortable: false,
      filterable: false,
    },
    { field: "id", headerName: "ID", minWidth: 50, flex: 0.5 },
    { field: "employee", headerName: "Employee Name", minWidth: 150, flex: 1 },
    {
      field: "requestDate",
      headerName: "Request Date",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const statusColors: { [key: string]: "default" | "success" | "error" } =
          {
            Pending: "default",
            Completed: "success",
            Rejected: "error",
          };
        return (
          <Chip
            label={params.value}
            variant="outlined"
            color={statusColors[params.value as keyof typeof statusColors]}
            sx={{
              fontSize: isSmallScreen ? "10px" : "13px",
              textTransform: "uppercase",
              borderWidth: "2px",
              borderRadius: "8px",
              padding: "2px 6px",
            }}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 200,
      flex: 1.2,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleOpenModal(params.row)}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: isSmallScreen ? "10px" : "14px",
            }}
          >
            Accept
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CancelIcon />}
            onClick={() => console.log("Rejected request for:", params.row)}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: isSmallScreen ? "10px" : "14px",
            }}
          >
            Reject
          </Button>
        </Box>
      ),
    },
  ];

  // Pagination State
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Theme Customization
  const customTheme = createTheme({
    palette: {
      primary: {
        main: "#5c6bc0",
      },
      success: {
        main: "#4caf50",
      },
      error: {
        main: "#f44336",
      },
    },
    typography: {
      fontSize: isSmallScreen ? 12 : 14,
      fontFamily: "Roboto, Arial",
    },
  });

  return (
    <ThemeProvider theme={customTheme}>
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          overflowX: "auto", // Enable horizontal scrolling
          bgcolor: "background.default",
          borderRadius: 3,
          boxShadow: 2,
        }}
      >
        <DataGrid
          rows={requestRows}
          columns={requestColumns}
          components={{
            Toolbar: () => (
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  bgcolor: "background.paper",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ fontWeight: "bold", ml: 2 }}
                >
                  Shift Requests
                </Typography>
                <GridToolbarQuickFilter
                  placeholder="Search requests..."
                  debounceMs={500}
                  quickFilterParser={(value) => value.split(" ")}
                  sx={{
                    width: "100%",
                    maxWidth: isSmallScreen ? 200 : 300,
                    "& .MuiInputBase-root": {
                      borderRadius: "5px",
                      paddingLeft: "15px",
                      bgcolor: "#f4f4f4",
                      border: "1px solid #d6d6d6",
                    },
                  }}
                />
              </Box>
            ),
          }}
          checkboxSelection
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f4f4f4",
              fontSize: isSmallScreen ? "12px" : "14px",
            },
            "& .MuiDataGrid-cell": {
              fontSize: isSmallScreen ? "10px" : "14px",
            },
            "& .MuiDataGrid-row:hover": {
              bgcolor: "#e3f2fd",
            },
          }}
        />

        {/* Confirmation Modal */}
        <Dialog open={modalOpen} onClose={handleCloseModal}>
          <DialogTitle>Confirm Shift Request</DialogTitle>
          <DialogContent>
            Are you sure you want to accept the shift request for{" "}
            {selectedRow?.employee}?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleConfirm} color="primary" variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default RequestShifts;
